import React from 'react';
import { CircularProgress } from '@mui/material';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { useDoraSummary } from './useDoraSummary';
import './ArtemyStyles.css';

const teamData = [
  { team: 'Team Atlas', w4: 78, w2: 85, now: 91, trend: '+13', up: true },
  { team: 'Team Helios', w4: 72, w2: 66, now: 58, trend: '-14', up: false },
  { team: 'Team Orion', w4: 68, w2: 70, now: 72, trend: '+4', up: true },
  { team: 'Team Nova', w4: 80, w2: 82, now: 87, trend: '+7', up: true },
  { team: 'Platform Core', w4: 79, w2: 81, now: 83, trend: '+4', up: true },
];

const trendPoints = [
  { label: 'Dec 1', score: 62 }, { label: 'Dec 8', score: 65 }, { label: 'Dec 15', score: 68 },
  { label: 'Dec 22', score: 70 }, { label: 'Dec 29', score: 74 }, { label: 'Jan 5', score: 76 },
  { label: 'Jan 12', score: 77 }, { label: 'Jan 19', score: 78 },
];

const doraVsElite = [
  { metric: 'Deploy Frequency', current: '4.2/day', elite: 'On-demand', status: 'Elite', color: 'var(--artemy-green)' },
  { metric: 'Lead Time', current: '2.8 days', elite: '< 1 day', status: 'High', color: 'var(--artemy-yellow)' },
  { metric: 'Change Fail Rate', current: '6.1%', elite: '< 5%', status: 'High', color: 'var(--artemy-yellow)' },
  { metric: 'MTTR', current: '47 min', elite: '< 1 hour', status: 'Elite', color: 'var(--artemy-green)' },
];

export default function TrendAnalysis() {
  const { dora } = useDoraSummary();

  return (
    <div className="artemy-fade-up">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 22 }}>
        <div>
          <div className="artemy-page-title">Trend Analysis</div>
          <div className="artemy-page-sub">Historical delivery performance and confidence trajectory</div>
        </div>
        <button className="artemy-btn artemy-btn-ghost" style={{ fontSize: 13 }}>
          Last 30 days ▾
        </button>
      </div>

      {/* Trend chart */}
      <div className="artemy-card" style={{ overflow: 'hidden', marginBottom: 16 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--artemy-border)' }}>
          <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 14, fontWeight: 600 }}>📈 Confidence Score — 30 Day Trend</div>
        </div>
        <div style={{ padding: 24 }}>
          <ResponsiveContainer width="100%" height={170}>
            <LineChart data={trendPoints}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--artemy-border)" vertical={false} />
              <XAxis dataKey="label" tick={{ fill: 'var(--artemy-text-dim)', fontSize: 10, fontFamily: 'var(--artemy-font-mono)' }} axisLine={false} tickLine={false} />
              <YAxis domain={[50, 100]} tick={{ fill: 'var(--artemy-text-dim)', fontSize: 10, fontFamily: 'var(--artemy-font-mono)' }} axisLine={false} tickLine={false} width={30} />
              <Tooltip
                contentStyle={{ background: 'var(--artemy-bg-surface)', border: '1px solid var(--artemy-border)', borderRadius: 8, color: 'var(--artemy-text-primary)', fontFamily: 'var(--artemy-font-mono)', fontSize: 11 }}
                cursor={{ stroke: 'rgba(255,255,255,0.1)' }}
              />
              <Line type="monotone" dataKey="score" stroke="var(--artemy-accent)" strokeWidth={3} dot={{ fill: 'var(--artemy-accent)', stroke: 'var(--artemy-bg-base)', strokeWidth: 2, r: 5 }} activeDot={{ r: 7, strokeWidth: 0 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* Team confidence table */}
        <div className="artemy-card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--artemy-border)' }}>
            <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 14, fontWeight: 600 }}>Team Confidence History</div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Team', '4 Wks Ago', '2 Wks Ago', 'Now', 'Trend'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '11px 16px', fontFamily: 'var(--artemy-font-mono)', fontSize: 10, letterSpacing: '.8px', color: 'var(--artemy-text-dim)', textTransform: 'uppercase', borderBottom: '1px solid var(--artemy-border)', fontWeight: 400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {teamData.map((t) => (
                <tr key={t.team} style={{ borderBottom: '1px solid rgba(255,255,255,.03)', transition: 'background .15s' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--artemy-text-primary)' }}>{t.team}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--artemy-text-secondary)' }}>{t.w4}%</td>
                  <td style={{ padding: '12px 16px', color: 'var(--artemy-text-secondary)' }}>{t.w2}%</td>
                  <td style={{ padding: '12px 16px', fontWeight: 700, color: t.now >= 80 ? 'var(--artemy-green)' : t.now >= 65 ? 'var(--artemy-yellow)' : 'var(--artemy-red)' }}>{t.now}%</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10, padding: '2px 8px', borderRadius: 4, display: 'inline-block', fontWeight: 600, color: t.up ? 'var(--artemy-green)' : 'var(--artemy-red)', background: t.up ? 'rgba(0,212,170,.1)' : 'rgba(255,77,106,.1)', border: `1px solid ${t.up ? 'var(--artemy-green)' : 'var(--artemy-red)'}33` }}>
                      {t.up ? '↑' : '↓'} {t.trend}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* DORA vs Elite */}
        <div className="artemy-card" style={{ overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--artemy-border)' }}>
            <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: 14, fontWeight: 600 }}>DORA vs Elite Benchmarks</div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Metric', 'Current', 'Elite Threshold', 'Status'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', padding: '11px 16px', fontFamily: 'var(--artemy-font-mono)', fontSize: 10, letterSpacing: '.8px', color: 'var(--artemy-text-dim)', textTransform: 'uppercase', borderBottom: '1px solid var(--artemy-border)', fontWeight: 400 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {doraVsElite.map((row) => (
                <tr key={row.metric} style={{ borderBottom: '1px solid rgba(255,255,255,.03)', transition: 'background .15s' }}>
                  <td style={{ padding: '12px 16px', fontWeight: 600, color: 'var(--artemy-text-primary)' }}>{row.metric}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--artemy-text-secondary)' }}>{row.current}</td>
                  <td style={{ padding: '12px 16px', color: 'var(--artemy-text-secondary)' }}>{row.elite}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: 10, padding: '2px 8px', borderRadius: 4, display: 'inline-block', color: row.color, background: `${row.color}1a`, border: `1px solid ${row.color}33`, textTransform: 'uppercase' }}>{row.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ height: 40 }} />
    </div>
  );
}
